import { useState } from 'react';

import { ChevronLeft, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';

import { useGetDocs } from '@/api/queries/useGetDocs';
import { Run } from '@/api/types/backend-dto-types';
import { Button } from '@/components/atoms/new-button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/molecules/table';
import formatDate from '@/helpers/formatDate';

interface UnprocessedDocsTableProps {
  caseId: string;
  runId: string;
  failedPagesCount: number;
  isVisible: boolean;
  onBackToRuns: () => void;
  selectedRun?: Run;
}

const UnprocessedDocsTable = ({
  caseId,
  failedPagesCount,
  runId,
  isVisible,
  onBackToRuns,
  selectedRun,
}: UnprocessedDocsTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;
  const { data: failedDocs, isLoading } = useGetDocs({
    caseId: isVisible ? caseId : null,
    page: currentPage,
    pageSize,
    processed: 'false',
    runId,
    searchQuery: '',
  });

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <div className="mb-4 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onBackToRuns}
            className="h-auto px-2 py-1 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Runs
          </Button>
          <span className="text-sm text-gray-500">Run ID: {selectedRun?.id}</span>
        </div>
        <h2 className="mb-1 text-base font-semibold">Failed Documents</h2>
        <p className="text-sm text-gray-500">
          {failedDocs?.totalCount && failedPagesCount
            ? `${failedDocs?.totalCount.toLocaleString()} documents (${failedPagesCount.toLocaleString()} pages) failed to process`
            : `Documents failed to process`}
          {selectedRun ? ` in the run started ${formatDate(selectedRun.createdDate, true, true)}` : ''}.
        </p>
      </div>

      <div className="h-full overflow-y-auto rounded-md border border-gray-200 bg-white">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="mt-12 flex h-full flex-col items-center gap-2">
              <Loader2 className="animate-spin text-gray-500" />
              <p className="text-sm text-gray-500">Loading failed documents...</p>
            </div>
          </div>
        ) : failedDocs?.docs && failedDocs.docs.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Upload Date</TableHead>
                <TableHead>Error Reason</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {failedDocs.docs.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-medium">{doc.file?.name || 'Unknown'}</TableCell>
                  <TableCell>{doc.file?.extension?.toUpperCase() || 'Unknown'}</TableCell>
                  <TableCell>{doc.file?.size ? `${Math.round(doc.file.size / 1024)} KB` : 'Unknown'}</TableCell>
                  <TableCell>{formatDate(doc.createdDate, true, true)}</TableCell>
                  <TableCell>
                    {doc.pipelineErrorType ? (
                      <span className="rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-red-200">
                        {doc.pipelineErrorType
                          .replace(/_/g, ' ')
                          .toLowerCase()
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    ) : (
                      <span className="text-gray-400">Unknown</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-gray-500">No failed documents found</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {!isLoading && failedDocs?.pageCount && failedDocs.pageCount > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {currentPage} of {failedDocs.pageCount}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === failedDocs.pageCount}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnprocessedDocsTable;
